import { isValidDate } from "../project/isValidDate";
import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";
import { normalizeApiMessage } from "lib/i18n/apiMessage";

const PARTICIPANTS_MAX_LENGTH = 500;

function normalizeApiMessageWithFallbackKey(
  payload,
  fallbackMessage,
  fallbackKey,
) {
  const normalized = normalizeApiMessage(payload, fallbackMessage);
  return {
    ...normalized,
    messageKey: /^(PROJECT_NODE_|INVALID_PROJECT_NODE_)/.test(payload?.code ?? "")
      ? "" : normalized.messageKey || fallbackKey || "",
  };
}

export async function PostCreateProject(form) {
  if (!isValidDate(form.period_start) || !isValidDate(form.period_end)) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Invalid period date." },
        "Invalid period date.",
        "api.project.create.validation.invalidPeriod",
      ),
    };
  }

  const hasEmptyField = ["project_name", "contract_name", "period_start", "period_end", "contractor", "participants", "purpose"].some((key) => {
    const value = form[key];
    if (typeof value !== "string") return !value;
    return value.trim() === "";
  });

  if (hasEmptyField) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "All fields are required." },
        "All fields are required.",
        "api.project.create.validation.requiredFields",
      ),
    };
  }

  if (String(form.participants ?? "").trim().length > PARTICIPANTS_MAX_LENGTH) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Participants must be 500 characters or less." },
        "Participants must be 500 characters or less.",
        "api.project.create.validation.participantsMaxLength",
      ),
    };
  }

  try {
    const path = `${BACKEND_URL}/project/create_project`;
    const res = await authFetch(
      path,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      },
      {
        refreshUrlIncludes: "auth/refresh",
      },
    );

    if (res.status === 401) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "Session expired. Please sign in again." },
          "Session expired. Please sign in again.",
          "api.auth.token.expired",
        ),
      };
    }

    const result = await res.json().catch(() => null);
    if (!result) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "Failed to parse server response." },
          "Failed to parse server response.",
          "api.common.serverError",
        ),
      };
    }

    if (!res.ok || result?.success !== true) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          result,
          "Failed to create project.",
          "api.project.create.failed",
        ),
      };
    }

    return {
      success: true,
      ...normalizeApiMessageWithFallbackKey(
        result,
        "Project created successfully.",
        "api.project.create.success",
      ),
      project_id: result.data?.id,
      project_name: result.data?.project_name,
    };
  } catch (error) {
    console.error("PostCreateProject request failed:", error);
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Server request failed." },
        "Server request failed.",
        "api.common.networkError",
      ),
    };
  }
}
